'use client';

import { c as _c } from "react/compiler-runtime";
import { cn } from '@morpho-org/ui-v6/utils';
import { HoverCard, HoverCardContent, HoverCardTrigger, Icon, Popover, PopoverContent, PopoverTrigger } from '@repo/ui';
import { Alert, WarningTriangle } from '@repo/ui/icons';
import { forwardRef, type HTMLAttributes, type ReactElement, type ReactNode, useState } from 'react';
import { useIsBelowBreakpoint } from '../../hooks/useMediaQuery';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type WarningLevel = 'RED' | 'YELLOW' | 'GREEN';
export interface Warning {
  level: WarningLevel;
  message: string | ReactNode;
  entity?: ReactNode;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

// Per-severity presentational style — text color, background tint, and icon
// travel together so they can't drift apart. Error keeps the circle icon ("stays
// as it is"); warnings use a triangle so the two read differently even when their
// colors are close (light mode).
const WARNING_LEVEL_STYLE: Record<WarningLevel, {
  text: string;
  bg: string;
  icon: ReactElement;
}> = {
  RED: {
    text: 'text-destructive-foreground',
    bg: 'bg-destructive',
    icon: <Alert />
  },
  YELLOW: {
    text: 'text-warning-foreground',
    bg: 'bg-warning',
    icon: <WarningTriangle />
  },
  GREEN: {
    text: '',
    bg: 'bg-muted',
    icon: <Alert />
  }
};
function severityRank(level: WarningLevel): number {
  switch (level) {
    case 'RED':
      return 2;
    case 'YELLOW':
      return 1;
    default:
      return 0;
  }
}
function getWorst(warnings: Warning[]): Warning {
  return warnings.reduce((a, b) => severityRank(b.level) > severityRank(a.level) ? b : a);
}
function sortByLevel(warnings: Warning[]): Warning[] {
  return [...warnings].toSorted((a, b) => severityRank(b.level) - severityRank(a.level));
}

// ---------------------------------------------------------------------------
// WarningHoverCardContent
// ---------------------------------------------------------------------------

function WarningHoverCardContent(t0) {
  const $ = _c(16);
  const {
    warnings
  } = t0;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  if ($[0] !== warnings) {
    const sorted = sortByLevel(warnings);
    const label = `${sorted.length} active warning${sorted.length > 1 ? "s" : ""}`;
    t3 = "gap-xs p-sm flex max-h-[300px] flex-col overflow-auto";
    t4 = _temp;
    if ($[6] !== label) {
      t5 = <span className="text-foreground text-sm">{label}</span>;
      $[6] = label;
      $[7] = t5;
    } else {
      t5 = $[7];
    }
    t1 = "gap-xs flex flex-col";
    t2 = sorted.map(_temp2);
    $[0] = warnings;
    $[1] = t1;
    $[2] = t2;
    $[3] = t3;
    $[4] = t4;
    $[5] = t5;
  } else {
    t1 = $[1];
    t2 = $[2];
    t3 = $[3];
    t4 = $[4];
    t5 = $[5];
  }
  let t6;
  if ($[8] !== t1 || $[9] !== t2) {
    t6 = <div className={t1}>{t2}</div>;
    $[8] = t1;
    $[9] = t2;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t3 || $[12] !== t4 || $[13] !== t5 || $[14] !== t6) {
    t7 = <div className={t3} onClick={t4}>{t5}{t6}</div>;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  return t7;
}

// ---------------------------------------------------------------------------
// WarningIcon
// ---------------------------------------------------------------------------
function _temp2(warning, index) {
  const style = WARNING_LEVEL_STYLE[warning.level];
  return <div key={index} className={cn("gap-2xs p-xs flex items-start rounded-lg", style.bg)}><div className="gap-2xs flex flex-col items-start"><div className="gap-2xs flex items-start"><Icon icon={style.icon} className={style.text} /><span className={cn("text-xs", style.text)}>{warning.message}</span></div>{warning.entity}</div></div>;
}
function _temp(e) {
  return e.stopPropagation();
}
export interface WarningIconProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  /** List of warnings to display */
  warnings: Warning[];
}

/**
 * WarningIcon — shows an alert icon colored by worst severity.
 *
 * On hover (desktop) or tap (mobile) displays a card listing all warnings.
 *
 * @example
 * ```tsx
 * <WarningIcon warnings={[{ level: "YELLOW", message: "Short timelock" }]} />
 * ```
 */
const WarningIcon = forwardRef<HTMLDivElement, WarningIconProps>((t0, ref) => {
  const $ = _c(34);
  let className;
  let props;
  let warnings;
  if ($[0] !== t0) {
    ({
      warnings,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = warnings;
  } else {
    className = $[1];
    props = $[2];
    warnings = $[3];
  }
  const [isOpen, setIsOpen] = useState(false);
  const isMobile = useIsBelowBreakpoint(768);
  if (warnings.length === 0) {
    return null;
  }
  const worst = getWorst(warnings);
  const worstStyle = WARNING_LEVEL_STYLE[worst.level];
  const colorClass = severityRank(worst.level) > 0 ? worstStyle.text : undefined;
  let t1;
  if ($[4] !== className) {
    t1 = cn("flex items-center", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== colorClass || $[7] !== worstStyle.icon) {
    t2 = <Icon icon={worstStyle.icon} className={colorClass} />;
    $[6] = colorClass;
    $[7] = worstStyle.icon;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== props || $[10] !== ref || $[11] !== t1 || $[12] !== t2) {
    t3 = <div ref={ref} className={t1} onClick={_temp3} {...props}>{t2}</div>;
    $[9] = props;
    $[10] = ref;
    $[11] = t1;
    $[12] = t2;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  const iconElement = t3;
  let t4;
  if ($[14] !== warnings) {
    t4 = <WarningHoverCardContent warnings={warnings} />;
    $[14] = warnings;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  const content = t4;
  if (isMobile) {
    let t5;
    if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = e_0 => {
        e_0.preventDefault();
        e_0.stopPropagation();
        setIsOpen(_temp4);
      };
      $[16] = t5;
    } else {
      t5 = $[16];
    }
    let t6;
    if ($[17] !== iconElement) {
      t6 = <PopoverTrigger asChild={true}><div onClick={t5}>{iconElement}</div></PopoverTrigger>;
      $[17] = iconElement;
      $[18] = t6;
    } else {
      t6 = $[18];
    }
    let t7;
    if ($[19] !== content) {
      t7 = <PopoverContent side="top" sideOffset={8} className="rounded-lg p-0">{content}</PopoverContent>;
      $[19] = content;
      $[20] = t7;
    } else {
      t7 = $[20];
    }
    let t8;
    if ($[21] !== isOpen || $[22] !== t6 || $[23] !== t7) {
      t8 = <Popover open={isOpen} onOpenChange={setIsOpen}>{t6}{t7}</Popover>;
      $[21] = isOpen;
      $[22] = t6;
      $[23] = t7;
      $[24] = t8;
    } else {
      t8 = $[24];
    }
    return t8;
  }
  let t5;
  if ($[25] !== iconElement) {
    t5 = <HoverCardTrigger asChild={true}>{iconElement}</HoverCardTrigger>;
    $[25] = iconElement;
    $[26] = t5;
  } else {
    t5 = $[26];
  }
  let t6;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      minWidth: "275px",
      maxWidth: "315px"
    };
    $[27] = t6;
  } else {
    t6 = $[27];
  }
  let t7;
  if ($[28] !== content) {
    t7 = <HoverCardContent side="top" sideOffset={8} onClick={_temp5} className="rounded-[20px] p-0" style={t6}>{content}</HoverCardContent>;
    $[28] = content;
    $[29] = t7;
  } else {
    t7 = $[29];
  }
  let t8;
  if ($[30] !== isOpen || $[31] !== t5 || $[32] !== t7) {
    t8 = <HoverCard open={isOpen} onOpenChange={setIsOpen} openDelay={200} closeDelay={100}>{t5}{t7}</HoverCard>;
    $[30] = isOpen;
    $[31] = t5;
    $[32] = t7;
    $[33] = t8;
  } else {
    t8 = $[33];
  }
  return t8;
});
WarningIcon.displayName = 'WarningIcon';
export { WarningIcon };
function _temp3(e) {
  return e.stopPropagation();
}
function _temp4(prev) {
  return !prev;
}
function _temp5(e_1) {
  return e_1.stopPropagation();
}